/**
 * Name formatting utilities for consistent display across components
 */

/**
 * Format a player's first and last name for display, omitting null/undefined values.
 * @param {{ first_name?: string|null, last_name?: string|null }} player - Player object
 * @returns {string} - Formatted name (e.g. "John Doe" or "John" if last_name is null)
 */
export const formatPlayerName = (player) => {
  if (!player) return '';
  return [player.first_name, player.last_name].filter(Boolean).join(' ').trim();
};

/**
 * Truncate a name to a maximum length and add ellipsis
 * @param {string} name - The name to truncate
 * @param {number} maxLength - Maximum length before truncation (default: 20)
 * @returns {string} - Truncated name with ellipsis if needed
 */
export const truncateName = (name, maxLength = 20) => {
  if (!name) return '';
  if (name.length <= maxLength) return name;
  return name.substring(0, maxLength - 3) + '...';
};

/**
 * Get initials from a full name
 * @param {string} name - The full name
 * @returns {string} - Initials (e.g., "John Doe" -> "JD")
 */
export const getInitials = (name) => {
  if (!name) return '';
  return name
    .split(' ')
    .map(word => word.charAt(0))
    .join('')
    .toUpperCase()
    .substring(0, 2);
};

/**
 * Shorten surname to first letter for compact display
 * @param {string} name - The full name
 * @returns {string} - First name + surname initial (e.g., "Damon Chow" -> "Damon C.")
 */
export const shortenSurname = (name) => {
  if (!name) return '';
  const parts = name.trim().split(' ');
  
  if (parts.length === 1) {
    // Only first name, return as-is
    return parts[0];
  }
  
  // First name + surname initial with period
  const firstName = parts[0];
  const surnameInitial = parts[parts.length - 1].charAt(0).toUpperCase();
  return `${firstName} ${surnameInitial}.`;
};

/**
 * Format a name for display with fallback options
 * @param {string} name - The name to format
 * @param {Object} options - Formatting options
 * @param {number} options.maxLength - Maximum length before truncation
 * @param {boolean} options.useInitials - Whether to use initials for very long names
 * @param {number} options.initialsThreshold - Length threshold to switch to initials
 * @returns {Object} - Formatted name object with display text and tooltip
 */
export const formatNameForDisplay = (name, options = {}) => {
  const {
    maxLength = 20,
    useInitials = true,
    initialsThreshold = 30
  } = options;

  if (!name) {
    return { display: '', tooltip: '' };
  }

  // For very long names, use initials if enabled
  if (useInitials && name.length > initialsThreshold) {
    const initials = getInitials(name);
    return {
      display: initials,
      tooltip: name,
      isInitials: true
    };
  }

  // For moderately long names, truncate
  if (name.length > maxLength) {
    return {
      display: truncateName(name, maxLength),
      tooltip: name,
      isTruncated: true
    };
  }

  // For short names, use as-is
  return {
    display: name,
    tooltip: '',
    isTruncated: false,
    isInitials: false
  };
};

/**
 * Get CSS classes for name display based on formatting
 * @param {Object} formatResult - Result from formatNameForDisplay
 * @param {string} baseClasses - Base CSS classes
 * @returns {string} - CSS classes for the name element
 */
export const getNameDisplayClasses = (formatResult, baseClasses = '') => {
  const classes = [baseClasses];
  
  if (formatResult.isInitials) {
    classes.push('font-bold text-xs');
  } else if (formatResult.isTruncated) {
    classes.push('truncate');
  }
  
  return classes.filter(Boolean).join(' ');
};

/**
 * Test function to demonstrate name formatting
 * @returns {Object} - Test results
 */
export const testNameFormatting = () => {
  const testNames = [
    'John',
    'John Doe',
    'John Michael Doe',
    'John Michael Alexander Doe',
    'John Michael Alexander Bartholomew Doe',
    'John Michael Alexander Bartholomew Christopher Doe',
    'John Michael Alexander Bartholomew Christopher Smith Doe'
  ];

  console.log('Testing Name Formatting:');
  console.log('========================');
  
  testNames.forEach(name => {
    const result = formatNameForDisplay(name, { maxLength: 15, useInitials: true, initialsThreshold: 25 });
    console.log(`"${name}" (${name.length} chars) -> "${result.display}" ${result.isInitials ? '(initials)' : result.isTruncated ? '(truncated)' : '(normal)'}`);
    if (result.tooltip) {
      console.log(`  Tooltip: "${result.tooltip}"`);
    }
  });

  return {
    testNames,
    results: testNames.map(name => formatNameForDisplay(name, { maxLength: 15, useInitials: true, initialsThreshold: 25 }))
  };
};

// Expose test function in browser environment
if (typeof window !== 'undefined') {
  window.testNameFormatting = testNameFormatting;
} 